import { MediaDisplay } from './MediaDisplay';

/**
 * MetaReelsMockup - Instagram Reels 9:16 Ad Mockup
 *
 * Dimensions: 350px × 697px (9:16 content area + bottom nav)
 * Based on Figma community mockup scaled from 440px to 350px (0.7955x)
 *
 * Structure (matching Instagram Reels ad format):
 * - Top: Back arrow, "Reels" title, camera icon
 * - Full-screen Background: 9:16 image/video
 * - Bottom Left: Avatar, username, "Sponsored", caption, music pill
 * - Bottom Right: Like, comment, share, more dots, music disc
 * - CTA Button: Above bottom nav
 * - Bottom Nav: Home, search, add, reels, profile
 *
 * Usage:
 *   <MetaReelsMockup
 *     username="fordmotorcompany"
 *     caption="Discover the all-new Ford Mustang"
 *     imageUrl="/path/to/image.jpg"
 *     ctaText="Learn More"
 *   />
 */

interface MetaReelsMockupProps {
  username?: string;
  profilePicUrl?: string;
  caption?: string;
  imageUrl?: string;
  contentType?: string | null;
  ctaText?: string;
  likeCount?: string;
  commentCount?: string;
  shareCount?: string;
  musicText?: string;
}

export function MetaReelsMockup({
  username = 'fordmotorcompany',
  profilePicUrl,
  caption = 'Primary text placeholder\u2026',
  imageUrl,
  contentType,
  ctaText = 'Learn More',
  likeCount = '5,000',
  commentCount = '6,000',
  shareCount = '7,000',
  musicText = 'Original audio',
}: MetaReelsMockupProps) {
  return (
    <div
      className="relative mx-auto overflow-hidden"
      style={{ width: '360px', height: '715px', borderRadius: '22px' }}
    >
      {/* Video/Content Area */}
      <div className="relative" style={{ width: '360px', height: '640px' }}>
        {/* Full-screen Background Image/Video */}
        <div className="absolute inset-0">
          {imageUrl ? (
            <MediaDisplay
              url={imageUrl}
              contentType={contentType}
              alt="Reels ad"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gray-900 flex items-center justify-center">
              <div className="text-center text-white/50">
                <div className="text-3xl font-bold mb-2">9:16</div>
                <div className="text-sm">1080x1920px recommended</div>
              </div>
            </div>
          )}
        </div>

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/60" />

        {/* Top Header - Back, Reels, Camera */}
        <div
          className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between"
          style={{ padding: '15px 15px 0' }}
        >
          {/* Back arrow */}
          <svg
            width="7"
            height="14"
            viewBox="0 0 7 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 1L1 7L6 13"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          {/* Reels title */}
          <span
            className="text-white font-semibold"
            style={{ fontSize: '15px', lineHeight: '18px' }}
          >
            Reels
          </span>

          {/* Camera icon */}
          <svg
            width="21"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle
              cx="12"
              cy="13"
              r="4"
              stroke="white"
              strokeWidth="2"
            />
          </svg>
        </div>

        {/* Bottom Content - Left bar + Right bar */}
        <div
          className="absolute left-0 right-0 z-20 flex items-end"
          style={{ bottom: '0px', padding: '15px', gap: '13px' }}
        >
          {/* Left Bar */}
          <div className="flex-1 flex flex-col" style={{ gap: '13px' }}>
            {/* Username row */}
            <div className="flex items-center" style={{ gap: '9px' }}>
              {/* Avatar */}
              <div
                className="rounded-full overflow-hidden bg-gray-700 flex items-center justify-center flex-shrink-0"
                style={{ width: '28px', height: '28px' }}
              >
                {profilePicUrl ? (
                  <img
                    src={profilePicUrl}
                    alt={username}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-white font-bold" style={{ fontSize: '11px' }}>
                    {username.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>

              {/* Username */}
              <span
                className="text-white font-semibold"
                style={{ fontSize: '11px', lineHeight: '14px' }}
              >
                {username}
              </span>

              {/* Sponsored label */}
              <span
                className="text-white/70"
                style={{ fontSize: '11px', lineHeight: '14px' }}
              >
                • Sponsored
              </span>
            </div>

            {/* Caption */}
            <p
              className="text-white line-clamp-2"
              style={{
                fontSize: '13px',
                lineHeight: '16px',
                letterSpacing: '-0.01em',
              }}
            >
              {caption}
            </p>

            {/* Music pill */}
            <div className="flex items-center" style={{ gap: '6px' }}>
              <div
                className="flex items-center"
                style={{
                  padding: '4px 9px',
                  background: 'rgba(0, 0, 0, 0.15)',
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                  borderRadius: '24px',
                  gap: '7px',
                }}
              >
                {/* Music note icon */}
                <svg
                  width="9"
                  height="10"
                  viewBox="0 0 12 13"
                  fill="white"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M11.5 0.5V9C11.5 10.38 10.38 11.5 9 11.5C7.62 11.5 6.5 10.38 6.5 9C6.5 7.62 7.62 6.5 9 6.5C9.53 6.5 10.02 6.67 10.42 6.94V3L4.5 4.36V10.5C4.5 11.88 3.38 13 2 13C0.62 13 -0.5 11.88 -0.5 10.5C-0.5 9.12 0.62 8 2 8C2.53 8 3.02 8.17 3.42 8.44V1.5L11.5 0.5Z" />
                </svg>
                <span
                  className="text-white"
                  style={{ fontSize: '11px', lineHeight: '10px' }}
                >
                  {musicText}
                </span>
              </div>
            </div>

            {/* CTA Button */}
            <button
              className="w-full flex items-center justify-center text-white font-semibold"
              style={{
                height: '36px',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(8px)',
                borderRadius: '8px',
                fontSize: '13px',
                letterSpacing: '0.3px',
              }}
            >
              {ctaText}
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="ml-2"
              >
                <path
                  d="M9 18L15 12L9 6"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {/* Right Bar - Actions */}
          <div
            className="flex flex-col items-center flex-shrink-0"
            style={{ width: '28px', gap: '22px' }}
          >
            {/* Like */}
            <div className="flex flex-col items-center" style={{ gap: '6px' }}>
              <svg
                width="22"
                height="20"
                viewBox="0 0 24 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M20.84 2.61a5.5 5.5 0 0 0-7.78 0L12 3.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 19.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span
                className="text-white font-semibold text-center"
                style={{ fontSize: '11px', lineHeight: '14px', letterSpacing: '-0.02em' }}
              >
                {likeCount}
              </span>
            </div>

            {/* Comment */}
            <div className="flex flex-col items-center" style={{ gap: '6px' }}>
              <svg
                width="23"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span
                className="text-white font-semibold text-center"
                style={{ fontSize: '11px', lineHeight: '14px', letterSpacing: '-0.02em' }}
              >
                {commentCount}
              </span>
            </div>

            {/* Share */}
            <div className="flex flex-col items-center" style={{ gap: '6px' }}>
              <svg
                width="21"
                height="18"
                viewBox="0 0 24 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span
                className="text-white font-semibold text-center"
                style={{ fontSize: '11px', lineHeight: '14px', letterSpacing: '-0.02em' }}
              >
                {shareCount}
              </span>
            </div>

            {/* More dots (horizontal) */}
            <div className="flex items-center" style={{ gap: '3px' }}>
              <div className="bg-white rounded-full" style={{ width: '3px', height: '3px' }} />
              <div className="bg-white rounded-full" style={{ width: '3px', height: '3px' }} />
              <div className="bg-white rounded-full" style={{ width: '3px', height: '3px' }} />
            </div>

            {/* Music disc */}
            <div
              className="rounded-lg overflow-hidden border-2 border-white bg-gray-700 flex items-center justify-center"
              style={{ width: '28px', height: '28px' }}
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 12 13"
                fill="white"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M11.5 0.5V9C11.5 10.38 10.38 11.5 9 11.5C7.62 11.5 6.5 10.38 6.5 9C6.5 7.62 7.62 6.5 9 6.5C9.53 6.5 10.02 6.67 10.42 6.94V3L4.5 4.36V10.5C4.5 11.88 3.38 13 2 13C0.62 13 -0.5 11.88 -0.5 10.5C-0.5 9.12 0.62 8 2 8C2.53 8 3.02 8.17 3.42 8.44V1.5L11.5 0.5Z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation Bar */}
      <div
        className="flex items-center justify-between bg-black"
        style={{ height: '45px', padding: '0 15px' }}
      >
        {/* Home */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <polyline points="9 22 9 12 15 12 15 22" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>

        {/* Search */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="11" cy="11" r="8" stroke="white" strokeWidth="2" />
          <path d="M21 21l-4.35-4.35" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>

        {/* Add/Create */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="3" width="18" height="18" rx="2" stroke="white" strokeWidth="2" />
          <path d="M12 8v8M8 12h8" stroke="white" strokeWidth="2" strokeLinecap="round" />
        </svg>

        {/* Reels (active) */}
        <svg width="18" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="2" width="20" height="20" rx="4" stroke="white" strokeWidth="2" />
          <path d="M2 8h20M8 2l-2 6M16 2l-2 6" stroke="white" strokeWidth="2" strokeLinecap="round" />
          <path d="M10 14l5-3-5-3v6z" fill="white" />
        </svg>

        {/* Profile */}
        <div
          className="rounded-full overflow-hidden bg-gray-600 flex items-center justify-center"
          style={{ width: '22px', height: '22px' }}
        >
          {profilePicUrl ? (
            <img src={profilePicUrl} alt="Profile" className="w-full h-full object-cover" />
          ) : (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          )}
        </div>
      </div>

      {/* Home Indicator */}
      <div className="flex justify-center bg-black" style={{ padding: '10px 0 8px' }}>
        <div
          className="bg-white rounded-full"
          style={{ width: '132px', height: '5px' }}
        />
      </div>
    </div>
  );
}
